import { useState } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import { useUserContext } from "../../context/UserContext";

const API_BASE = process.env.REACT_APP_API_URL;

const MODEL_OPTIONS = [
  { value: "default", label: "Default (Gauge-ro 1.0)" },
  { value: "ML002", label: "ML002" },
];

function ResultTable({ title, columns, data, emptyMsg }) {
  if (!data || data.length === 0) return null;
  return (
    <div className="mt-4">
      <h3 className="text-md font-semibold text-gray-700 mb-2">{title}</h3>
      <table className="w-full text-left border-collapse">
        <thead>
          <tr className="bg-gray-100 border-b border-gray-200">
            {columns.map((col) => (
              <th key={col.key} className="p-3 font-semibold text-gray-600 text-sm">{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr><td colSpan={columns.length} className="p-3 text-gray-500">{emptyMsg || "No data"}</td></tr>
          ) : (
            data.map((row, i) => (
              <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                {columns.map((col) => (
                  <td key={col.key} className="p-3 text-gray-700 text-sm">{row[col.key] ?? "-"}</td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function SimpleList({ title, items }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="mt-4">
      <h3 className="text-md font-semibold text-gray-700 mb-2">{title}</h3>
      <div className="flex flex-wrap gap-2">
        {items.map((item, i) => (
          <span key={i} className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-sm font-mono">{item}</span>
        ))}
      </div>
    </div>
  );
}

export default function DeviceRegistration() {
  const { token, logout } = useUserContext();
  const [activeTab, setActiveTab] = useState("add");
  const [submitting, setSubmitting] = useState(false);

  const [deviceId, setDeviceId] = useState("");
  const [model, setModel] = useState("default");
  const [addResult, setAddResult] = useState(null);

  const [customerId, setCustomerId] = useState("");
  const [registerDeviceIdsText, setRegisterDeviceIdsText] = useState("");
  const [registerResult, setRegisterResult] = useState(null);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  function handleAuthErr(err) {
    if (err.response?.status === 401) {
      logout();
      toast.error("Session expired. Please login again.");
    }
  }

  async function handleAddDevice(e) {
    e.preventDefault();
    const id = deviceId.trim();
    if (!id) {
      toast.error("Device ID is required");
      return;
    }
    const device = model === "default" ? id : { device_id: id, model };
    setSubmitting(true);
    setAddResult(null);
    try {
      const res = await axios.post(`${API_BASE}/inventory/add-devices`, { devices: [device] }, { headers });
      setAddResult(res.data.data);
      toast.success(res.data.message || "Device added successfully");
      const added = res.data.data?.added || [];
      if (added.length > 0) {
        const existing = registerDeviceIdsText.trim();
        setRegisterDeviceIdsText(existing ? existing + "\n" + added[0].device_id : added[0].device_id);
      }
      setDeviceId("");
    } catch (err) {
      handleAuthErr(err);
      const msg = err.response?.data?.message || err.message || "Failed to add device";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleRegisterDevices(e) {
    e.preventDefault();
    if (!customerId.trim()) {
      toast.error("Customer ID is required");
      return;
    }
    const device_ids = registerDeviceIdsText.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);
    if (device_ids.length === 0) {
      toast.error("Enter at least one device ID");
      return;
    }
    setSubmitting(true);
    setRegisterResult(null);
    const payload = { customer_id: Number(customerId.trim()), device_ids };
    try {
      const res = await axios.post(`${API_BASE}/inventory/register-devices`, payload, { headers });
      setRegisterResult(res.data.data);
      toast.success(res.data.message || "Devices registered successfully");
    } catch (err) {
      handleAuthErr(err);
      const msg = err.response?.data?.message || err.message || "Failed to register devices";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="w-full min-h-screen px-6 py-4">
      <div className="rounded-xl bg-white p-6 shadow-sm">
        <h1 className="mb-1 text-2xl font-bold text-gray-900">Device Registration</h1>
        <p className="mb-6 text-sm text-gray-500">Add devices to inventory and register them to customers.</p>

        <div className="flex gap-1 mb-6 border-b border-gray-200">
          {[
            { key: "add", label: "Add Device" },
            { key: "register", label: "Register Device" },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
                activeTab === tab.key
                  ? "border-indigo-600 text-indigo-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === "add" && (
          <form onSubmit={handleAddDevice}>
            <div className="legacy-form">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Device ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. 2C65AA3C1C77"
                  value={deviceId}
                  onChange={(e) => setDeviceId(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 p-2.5 outline-none focus:border-indigo-500 text-sm"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
                <select
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 p-2.5 outline-none focus:border-indigo-500 text-sm bg-white"
                >
                  {MODEL_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="rounded-lg bg-indigo-600 px-6 py-2.5 font-semibold text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-300"
              >
                {submitting ? "Adding Device..." : "Add Device"}
              </button>
            </div>
          </form>
        )}

        {activeTab === "register" && (
          <form onSubmit={handleRegisterDevices}>
            <div className="legacy-form">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Customer ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  placeholder="e.g. 3162980346"
                  value={customerId}
                  onChange={(e) => setCustomerId(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 p-2.5 outline-none focus:border-indigo-500 text-sm"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Device IDs <span className="text-red-500">*</span>
                </label>
                <textarea
                  rows={5}
                  placeholder="ML002-ABC, DEVICE-123&#10;or one per line"
                  value={registerDeviceIdsText}
                  onChange={(e) => setRegisterDeviceIdsText(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 p-3 outline-none focus:border-indigo-500 text-sm font-mono"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Separate by comma or new line. Auto-populated from successful "Add Device" submissions.
                </p>
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="rounded-lg bg-indigo-600 px-6 py-2.5 font-semibold text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-300"
              >
                {submitting ? "Registering Devices..." : "Register Devices"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}